/**
 * MessageList - Native scroll message list with auto-scroll, infinite scroll, and grouping.
 * Uses column-reverse for bottom-anchored scrolling.
 */

import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import MessageItem from './MessageItem';
import TypingIndicator from './TypingIndicator';
import type { ChatMessage } from '../../types/chat';

function shouldGroup(current: ChatMessage, older: ChatMessage): boolean {
  if (current.user_id !== older.user_id) return false;
  if (current.is_deleted || older.is_deleted) return false;
  if (older.reply_count > 0) return false;
  const gap = new Date(current.created_at).getTime() - new Date(older.created_at).getTime();
  return Math.abs(gap) < 5 * 60 * 1000;
}

const MessageList: React.FC = () => {
  const { messages, currentChannel, loadMessages, hasMoreMessages, typingUsers } = useChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const loadingRef = useRef(false);
  const prevMessageCountRef = useRef(0);

  const channelTyping = currentChannel ? typingUsers.get(currentChannel.id) || [] : [];

  // In column-reverse: scrollTop is 0 at bottom, negative when scrolled up
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const atBottom = scrollRef.current.scrollTop >= -1;
    setIsAtBottom(atBottom);
    if (atBottom) setHasNewMessages(false);
  }, []);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setHasNewMessages(false);
  }, []);

  // Detect new messages when scrolled up
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current && !isAtBottom) {
      setHasNewMessages(true);
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, isAtBottom]);

  // IntersectionObserver for infinite scroll (loads older messages)
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreMessages && !loadingRef.current) {
          loadingRef.current = true;
          const oldest = messages[messages.length - 1];
          if (oldest && currentChannel) {
            loadMessages(currentChannel.id, oldest.id).finally(() => {
              loadingRef.current = false;
            });
          }
        }
      },
      { root: container, rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreMessages, messages, currentChannel, loadMessages]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 relative overflow-hidden">
        {/* New messages pill */}
        {hasNewMessages && !isAtBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-blue-600 text-white text-xs font-medium px-4 py-1.5 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          >
            New messages &darr;
          </button>
        )}

        {messages.length > 0 ? (
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto flex flex-col-reverse px-0"
          >
            {/* Messages render newest-first; column-reverse flips visual order */}
            {messages.map((msg, idx) => {
              const olderMsg = messages[idx + 1]; // next in array = older in time
              const isGrouped = olderMsg ? shouldGroup(msg, olderMsg) : false;
              return (
                <MessageItem
                  key={msg.id}
                  message={msg}
                  isGroupStart={!isGrouped}
                />
              );
            })}

            {/* Sentinel at the visual top (oldest end) for loading more */}
            {hasMoreMessages && (
              <div ref={sentinelRef} className="flex justify-center py-3">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            No messages yet
          </div>
        )}
      </div>
      {channelTyping.length > 0 && <TypingIndicator users={channelTyping} />}
    </div>
  );
};

export default MessageList;
